<template>
  <div class="card mb-3">
    <div class="card-header bg-light">
      <h5 class="mb-0">Datos sobre el gasto</h5>
    </div>
    <div class="card-body">
      <!-- Primera fila: Descripción, Nº factura, Fecha -->
      <div class="row">
        <div class="col-lg-4">
          <div class="form-group">
            <label for="material">
              {{ isFormativeAction ? 'Descripción del módulo económico' : 'Descripción del gasto' }}
            </label>
            <app-textarea-ai
              :value="localResume.material"
              @input="$emit('update:field', 'material', $event)"
              :enable-ai="true"
              :max-chars="5000"
              :rows="2"
              :placeholder="isFormativeAction ? 'Descripción del módulo económico' : 'Descripción del gasto'"
              id="material"
              textarea-class="form-control"
            ></app-textarea-ai>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="form-group">
            <label for="n_invoice">
              {{ isFormativeAction ? 'Nº participantes' : 'Nº de factura o referencia' }}
            </label>
            <input
              :value="localResume.n_invoice"
              @input="$emit('update:field', 'n_invoice', $event.target.value)"
              type="text"
              class="form-control"
              id="n_invoice"
              :placeholder="isFormativeAction ? 'Nº participantes' : 'Nº de factura o referencia'"
            />
          </div>
        </div>
        <div class="col-lg-4">
          <div class="form-group">
            <label for="invoice_date">
              {{ isFormativeAction ? 'Fecha inicio' : 'Fecha de factura' }}
            </label>
            <input
              :value="localResume.invoice_date"
              @input="$emit('update:field', 'invoice_date', $event.target.value)"
              type="date"
              class="form-control"
              id="invoice_date"
            />
          </div>
        </div>
      </div>
      <!-- Segunda fila: Importes del documento (Neto, IVA, Total) -->
      <div class="row">
        <div class="col-lg-4">
          <div class="form-group">
            <label for="net_cost">Importe neto en doc. probatorio del gasto</label>
            <input
              :value="localResume.net_cost"
              @input="$emit('update:field', 'net_cost', $event.target.value)"
              @blur="v.net_cost && v.net_cost.$touch()"
              type="number"
              step="0.01"
              class="form-control"
              :class="{ 'is-invalid': v.net_cost && v.net_cost.$error }"
              id="net_cost"
              placeholder="0.00"
            />
            <div v-if="v.net_cost && v.net_cost.$error" class="invalid-feedback">
              <div v-if="!v.net_cost.decimal">Debe ser un número decimal válido</div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="form-group">
            <label for="iva_cost">Importe IVA en doc. probatorio del gasto</label>
            <input
              :value="localResume.iva_cost"
              @input="$emit('update:field', 'iva_cost', $event.target.value)"
              @blur="v.iva_cost && v.iva_cost.$touch()"
              type="number"
              step="0.01"
              class="form-control"
              :class="{ 'is-invalid': v.iva_cost && v.iva_cost.$error }"
              id="iva_cost"
              placeholder="0.00"
            />
            <div v-if="v.iva_cost && v.iva_cost.$error" class="invalid-feedback">
              <div v-if="!v.iva_cost.decimal">Debe ser un número decimal válido</div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="form-group">
            <label for="total_cost">Importe total en doc. probatorio del gasto</label>
            <output
              id="total_cost"
              class="calculated-amount form-control d-flex align-items-center"
              for="net_cost iva_cost"
            >
              {{ displayTotalDocument }}
            </output>
            <small class="form-text text-muted">
              Calculado automáticamente: neto + IVA
            </small>
          </div>
        </div>
      </div>
      <!-- Tercera fila: Importes imputados (Neto imputado, IVA imputado, Total imputado) -->
      <div class="row">
        <div class="col-lg-4">
          <div class="form-group">
            <label for="net_imputed_cost">
              {{ isFormativeAction ? 'Importe módulo' : 'Importe neto imputado' }}
            </label>
            <input
              :value="localResume.net_imputed_cost"
              @input="$emit('update:field', 'net_imputed_cost', $event.target.value)"
              @blur="v.net_imputed_cost && v.net_imputed_cost.$touch()"
              type="number"
              step="0.01"
              class="form-control"
              :class="{ 'is-invalid': v.net_imputed_cost && v.net_imputed_cost.$error }"
              id="net_imputed_cost"
              placeholder="0.00"
            />
            <div v-if="v.net_imputed_cost && v.net_imputed_cost.$error" class="invalid-feedback">
              <div v-if="!v.net_imputed_cost.decimal">Debe ser un número decimal válido</div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="form-group">
            <label for="iva_imputed_cost">
              {{ isFormativeAction ? 'Nº horas' : 'Importe IVA o similar imputado' }}
            </label>
            <input
              :value="localResume.iva_imputed_cost"
              @input="$emit('update:field', 'iva_imputed_cost', $event.target.value)"
              @blur="v.iva_imputed_cost && v.iva_imputed_cost.$touch()"
              type="number"
              step="0.01"
              class="form-control"
              :class="{ 'is-invalid': v.iva_imputed_cost && v.iva_imputed_cost.$error }"
              id="iva_imputed_cost"
              placeholder="0.00"
            />
            <div v-if="v.iva_imputed_cost && v.iva_imputed_cost.$error" class="invalid-feedback">
              <div v-if="!v.iva_imputed_cost.decimal">Debe ser un número decimal válido</div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="form-group">
            <label for="total_imputed_cost">Importe total imputado</label>
            <output
              id="total_imputed_cost"
              class="calculated-amount form-control d-flex align-items-center bg-warning-light"
              for="net_imputed_cost iva_imputed_cost"
            >
              {{ displayTotalImputed }}
            </output>
            <small class="form-text text-muted">
              Calculado automáticamente: neto imputado + IVA imputado
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppTextareaAi from "@/components/forms/AppTextareaAi.vue";

export default {
  name: "ExpenseSection",
  components: {
    AppTextareaAi,
  },
  props: {
    localResume: {
      type: Object,
      required: true,
    },
    isFormativeAction: {
      type: Boolean,
      default: false,
    },
    v: {
      type: Object,
      required: true,
    },
  },
  computed: {
    displayTotalDocument() {
      return this.formatSumDisplay(
        this.localResume.net_cost,
        this.localResume.iva_cost
      );
    },
    displayTotalImputed() {
      return this.formatSumDisplay(
        this.localResume.net_imputed_cost,
        this.localResume.iva_imputed_cost
      );
    },
  },
  methods: {
    formatSumDisplay(a, b) {
      const n = Number.parseFloat(a);
      const i = Number.parseFloat(b);
      const net = Number.isFinite(n) ? n : 0;
      const iva = Number.isFinite(i) ? i : 0;
      return (net + iva).toFixed(2);
    },
  },
};
</script>

<style scoped>
.bg-warning-light {
  background-color: #fff3cd;
}

.calculated-amount {
  min-height: calc(1.5em + 0.75rem + 2px);
  background-color: #e9ecef;
  cursor: default;
  user-select: none;
}

.calculated-amount.bg-warning-light {
  background-color: #fff3cd;
}
</style>
